import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { MdArrowBack } from "react-icons/md";
import {
  FiBookOpen, FiAlertCircle, FiGift, FiPercent, FiTrendingUp,
  FiZap, FiRepeat, FiTarget, FiLayers, FiActivity, FiAward,
} from "react-icons/fi";
import { toast } from "react-hot-toast";

import { useGetRuleByIdMutation, useGetMetaSchemaQuery, useGetWalletsQuery } from "../../../features/tenant/rules/rulesApi";
import { fromRule, resolveSchemeIds } from "./builder/ruleModel";
import { plainEnglish } from "./builder/summary";
import { BuilderContext } from "./builder/BuilderContext";
import { Btn } from "./builder/ui";
import ViewCondition from "./builder/ViewCondition";
import RuleFlowPipeline from "./builder/RuleFlowPipeline";

// ─── Design primitives (matches ViewSchemeModal) ──────────────────────────────

const SectionTitle = ({ children }) => (
  <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
    <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest">{children}</span>
  </div>
);

const Row = ({ label, children, noBorder }) => (
  <div className={`flex items-start gap-3 px-4 py-2.5 ${noBorder ? "" : "border-b border-gray-100"}`}>
    <span className="text-xs text-gray-400 font-medium shrink-0 w-36 pt-0.5 leading-relaxed">{label}</span>
    <div className="text-sm text-gray-800 flex-1 min-w-0">{children ?? <span className="text-gray-300">—</span>}</div>
  </div>
);

const Chip = ({ children, className = "" }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border capitalize ${className || "bg-gray-100 text-gray-600 border-gray-200"}`}>
    {children}
  </span>
);

const StatusDot = ({ status }) => {
  const isActive = status === 1 || status === "active";
  const isDraft = status === "draft" || status === 0;
  if (isActive) return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-50 text-green-700 border border-green-200">
      <span className="w-1 h-1 rounded-full bg-green-500" />Active
    </span>
  );
  if (isDraft) return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-500 border border-gray-200">
      <span className="w-1 h-1 rounded-full bg-gray-400" />Draft
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-50 text-red-600 border border-red-200">
      <span className="w-1 h-1 rounded-full bg-red-400" />Inactive
    </span>
  );
};

function fmtDate(raw) {
  if (!raw) return null;
  const d = String(raw).split("T")[0];
  const [y, m, day] = d.split("-");
  return y && m && day ? `${day}/${m}/${y}` : null;
}

// ─── Action display (per type) ────────────────────────────────────────────────

const ACTION_ICONS = { CREDIT: FiGift, CREDIT_DYNAMIC: FiPercent, UPDATE_USER_SLAB: FiTrendingUp };

function ActionRows({ action, wallets }) {
  const Icon = ACTION_ICONS[action.type] || FiGift;

  const walletName = !action.wallet_type || action.wallet_type === "default"
    ? "Main Wallet"
    : (wallets || []).find((w) => String(w.scheme_id) === String(action.wallet_type))?.name || `Scheme #${action.wallet_type}`;

  return (
    <>
      <Row label="Action Type">
        <span className="inline-flex items-center gap-1.5">
          <Icon className="h-3.5 w-3.5 text-gray-500" />
          <Chip className="bg-blue-50 text-blue-700 border-blue-100">{action.type}</Chip>
        </span>
      </Row>

      {action.type === "CREDIT" && (
        <Row label="Points">
          <span className="text-sm font-semibold text-gray-900">+{action.amount ?? "—"} pts</span>
        </Row>
      )}

      {action.type === "CREDIT_DYNAMIC" && (
        <>
          <Row label="Base Field">
            <span className="font-mono text-xs bg-gray-100 border border-gray-200 px-1.5 py-0.5 rounded text-gray-700">{action.base_field || "—"}</span>
          </Row>
          <Row label="Multiplier">
            <span className="text-sm font-semibold text-gray-900">×{action.multiplier ?? "—"}</span>
          </Row>
        </>
      )}

      {action.type === "UPDATE_USER_SLAB" && (
        <>
          <Row label="Slab Name">{action.slab_name || "—"}</Row>
          <Row label="Slab Rank">#{action.slab_rank ?? "—"}</Row>
          <Row label="Outcome Key">
            <span className="font-mono text-xs bg-gray-100 border border-gray-200 px-1.5 py-0.5 rounded text-gray-700">{action.outcome_key || "—"}</span>
          </Row>
        </>
      )}

      {(action.type === "CREDIT" || action.type === "CREDIT_DYNAMIC") && (
        <Row label="Target Wallet">
          <span className="inline-flex items-center gap-1.5">
            <FiAward className="h-3.5 w-3.5 text-gray-400 shrink-0" />
            {walletName}
          </span>
        </Row>
      )}

      {action.description && (
        <Row label="Ledger Note">
          <span className="text-sm text-gray-600 italic">"{action.description}"</span>
        </Row>
      )}
    </>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function ViewRule() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  // Build the rules base path by stripping /view/:id from the current URL
  const rulesBase = location.pathname.replace(/\/view\/[^/]+\/?$/, "");

  const token = sessionStorage.getItem("access_token");
  const tenantId = JSON.parse(sessionStorage.getItem("vendorData") || "{}")?.tenant_id;
  const auth = { token, tenant_id: tenantId };

  const [getRule, { isLoading: ruleLoading, data: ruleData, error: ruleError }] = useGetRuleByIdMutation();
  const { data: meta } = useGetMetaSchemaQuery(auth, { skip: !token });
  const { data: wallets } = useGetWalletsQuery(auth, { skip: !token });

  const [rule, setRule] = useState(null);
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    if (id) getRule({ id, token, tenant_id: tenantId });
  }, [id]); // eslint-disable-line

  useEffect(() => {
    if (ruleData) {
      try {
        const raw = ruleData.body ?? ruleData.data ?? ruleData;
        let ruleObj = { ...raw };
        if (ruleObj.condition && typeof ruleObj.condition === "string") {
          try { ruleObj.condition = JSON.parse(ruleObj.condition); } catch {}
        }
        setRule(ruleObj);
        setDraft(fromRule(ruleObj));
      } catch {
        toast.error("Failed to process rule data");
      }
    }
    if (ruleError) toast.error("Failed to fetch rule");
  }, [ruleData, ruleError]);

  const schemeIds = useMemo(() => (draft ? resolveSchemeIds(draft.condition) : []), [draft]);

  const rewardModeLabel = { one_time: "One-time", repeating: "Repeating", milestone: "Milestone" };
  const rewardModeIcon = { one_time: FiZap, repeating: FiRepeat, milestone: FiTarget };
  const RewardIcon = rewardModeIcon[rule?.reward_mode] || FiZap;

  const eventLabel = meta?.event_types?.find((e) => e.name === rule?.event_type)?.label || rule?.event_type;
  const schemeNames = schemeIds.map(
    (sid) => (wallets || []).find((w) => String(w.scheme_id) === String(sid))?.name || `Scheme #${sid}`
  );

  // ── Loading ──
  if (ruleLoading || (!rule && !ruleError)) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3 text-gray-400">
        <div className="h-7 w-7 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
        <span className="text-sm">Loading rule…</span>
      </div>
    );
  }

  if (!rule) {
    return (
      <div className="m-6 rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700 flex items-start gap-2.5">
        <FiAlertCircle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold">Rule not found</p>
          <p className="text-xs text-red-500 mt-0.5">It may have been deleted or you may not have access.</p>
        </div>
      </div>
    );
  }

  const builderCtx = {
    token, tenantId,
    meta: meta || null,
    wallets: wallets || [],
    eventType: rule.event_type,
    appUserTypes: [], states: [],
    schemeFilters: null,
    schemeId: schemeIds[0] || null,
    schemeIds,
  };

  return (
    <BuilderContext.Provider value={builderCtx}>
      <div className="min-h-screen bg-gray-50 pb-12">

        {/* ── Sticky Header ── */}
        <div className="sticky top-0 z-20 bg-white border-b border-gray-200 shadow-sm">
          <div className="px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="p-1.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <MdArrowBack className="h-4 w-4" />
              </button>
              <div className="h-4 w-px bg-gray-200" />
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-sm font-semibold text-gray-900">{rule.name}</h1>
                <StatusDot status={rule.status} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Btn variant="ghost" onClick={() => navigate(`${rulesBase}/edit/${id}`)} className="text-xs">
                Edit
              </Btn>
              <Btn variant="ghost" onClick={() => navigate(`${rulesBase}/add`, { state: { cloneFrom: rule } })} className="text-xs">
                Clone Rule
              </Btn>
            </div>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="px-6 lg:px-8 pt-5">
          <div className="grid gap-5 lg:grid-cols-[1fr_340px] items-start">

            {/* ── Main Panel ── */}
            <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">

              {/* 1. Basic Info */}
              <SectionTitle>Basic Info</SectionTitle>

              {rule.description && (
                <Row label="Description">
                  <p className="text-sm text-gray-700 leading-relaxed">{rule.description}</p>
                </Row>
              )}

              <Row label="Event">
                <span className="inline-flex items-center gap-1.5">
                  {rule.event_type === "scheme_scan" ? (
                    <FiLayers className="h-3.5 w-3.5 text-indigo-500 shrink-0" />
                  ) : (
                    <FiActivity className="h-3.5 w-3.5 text-blue-500 shrink-0" />
                  )}
                  <Chip className="bg-indigo-50 text-indigo-700 border-indigo-100">{eventLabel || rule.event_type}</Chip>
                </span>
              </Row>

              {schemeNames.length > 0 && (
                <Row label={schemeNames.length > 1 ? "Target Schemes" : "Target Scheme"}>
                  <div className="flex flex-wrap gap-1">
                    {schemeNames.map((name, i) => (
                      <Chip key={i} className="bg-blue-50 text-blue-700 border-blue-100">{name}</Chip>
                    ))}
                  </div>
                </Row>
              )}

              <Row label="Rule Type">
                <Chip>{rule.rule_type}</Chip>
              </Row>

              <Row label="Reward Mode">
                <span className="inline-flex items-center gap-1.5">
                  <RewardIcon className="h-3.5 w-3.5 text-gray-500 shrink-0" />
                  {rewardModeLabel[rule.reward_mode] || rule.reward_mode}
                </span>
              </Row>

              <Row label="Priority">
                {rule.priority != null ? `P${rule.priority}` : "—"}
              </Row>

              <Row label="Valid From">
                <span className="font-mono text-sm">{fmtDate(rule.valid_from) || "—"}</span>
              </Row>

              <Row label="Valid Until" noBorder>
                {fmtDate(rule.valid_until)
                  ? <span className="font-mono text-sm">{fmtDate(rule.valid_until)}</span>
                  : <span className="text-xs text-green-600 font-medium">No expiry (ongoing)</span>
                }
              </Row>

              {/* 2. Conditions */}
              <SectionTitle>Trigger Conditions</SectionTitle>

              {rule.condition ? (
                <ViewCondition condition={rule.condition} meta={meta} wallets={wallets || []} depth={0} />
              ) : (
                <div className="px-4 py-3">
                  <p className="text-xs text-gray-400 italic">No conditions — fires on every matching event.</p>
                </div>
              )}

              {/* 3. Reward Action */}
              <SectionTitle>Reward Action</SectionTitle>

              {draft && (draft.actions || []).length > 0 ? (
                (draft.actions).map((action, i) => (
                  <ActionRows key={i} action={action} wallets={wallets || []} />
                ))
              ) : (
                <div className="px-4 py-3">
                  <p className="text-xs text-gray-400 italic">No actions configured.</p>
                </div>
              )}

              {/* 4. Limits */}
              <SectionTitle>Limits &amp; Caps</SectionTitle>

              <Row label="Max Triggers">
                {rule.max_triggers_per_user != null
                  ? <span>{rule.max_triggers_per_user}× per user (lifetime)</span>
                  : <span className="text-xs text-gray-500">Unlimited</span>
                }
              </Row>

              {rule.reward_mode === "milestone" && (
                <Row label="Milestone Interval">
                  {rule.milestone_interval ? `Every ${rule.milestone_interval}` : "—"}
                </Row>
              )}

              {rule.reward_mode === "repeating" && (
                <Row label="Cooldown" noBorder>
                  {rule.cooldown_hours != null ? `${rule.cooldown_hours}h between triggers` : "None"}
                </Row>
              )}
            </div>

            {/* ── Side Panel ── */}
            <div className="flex flex-col gap-4 sticky top-14">

              {/* Plain English */}
              <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                <SectionTitle>
                  <span className="inline-flex items-center gap-1.5">
                    <FiBookOpen className="h-3 w-3" />
                    In Plain English
                  </span>
                </SectionTitle>
                <div className="px-4 py-3">
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {draft && meta ? plainEnglish(draft, meta, wallets || []) : "Loading…"}
                  </p>
                </div>
              </div>

              {/* Rule Flow Pipeline */}
              {draft && (
                <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                  <SectionTitle>Rule Flow</SectionTitle>
                  <div className="p-3">
                    <RuleFlowPipeline draft={draft} />
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </BuilderContext.Provider>
  );
}
